/**
 * use-theme.ts — hook canonico para tema Sol/Noite.
 * Item 3.6 da Auditoria 2026.
 *
 * 3 modos:
 * - 'auto' (default) — respeita prefers-color-scheme do OS
 * - 'light' — forca Claro independente do OS
 * - 'dark' — forca Escuro independente do OS
 *
 * Aplica via attribute data-theme no <html>. Removendo attribute = auto.
 * Persiste em localStorage florasegura:theme.
 */
import { useEffect, useState } from 'react'

export type Theme = 'auto' | 'light' | 'dark'

const STORAGE_KEY = 'florasegura:theme'

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'auto') return v
  } catch { /* localStorage indisponivel */ }
  return 'auto'
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'auto') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }
}

function resolveEffective(theme: Theme): 'light' | 'dark' {
  if (theme !== 'auto') return theme
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/**
 * Hook canonico de tema. Retorna o estado atual + setter + tema efetivo.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = readStoredTheme()
    applyTheme(stored)
    return stored
  })

  const [effective, setEffective] = useState<'light' | 'dark'>(() => resolveEffective(readStoredTheme()))

  // Listen to OS preference changes (relevante quando theme === 'auto')
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(prefers-color-scheme: light)')
    function onChange() {
      if (theme === 'auto') {
        setEffective(mql.matches ? 'light' : 'dark')
      }
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [theme])

  function setTheme(t: Theme) {
    setThemeState(t)
    try { localStorage.setItem(STORAGE_KEY, t) } catch { /* */ }
    applyTheme(t)
    setEffective(resolveEffective(t))
  }

  return { theme, setTheme, effective }
}

/**
 * Aplica o tema persistido o mais cedo possivel (chame em main.tsx).
 * Garante que o app inicia ja com data-theme correto, evitando FOUC.
 */
export function applyStoredThemeImmediately() {
  applyTheme(readStoredTheme())
}
